// Staged commit actions for the in-place update verdicts (KAN-1464).
//
// Extracted from UpdateProductModal.tsx's write logic:
// - writeCoreUpdates mirrors the immediate-write block (~lines 913-945), but
//   only writes fields that actually changed (the modal wrote unconditionally;
//   changed-only is the staged-mode refinement described in the plan).
// - commitMinorEvolution mirrors the Minor Evolution dialog's "Yes" handler
//   (~lines 2477-2533): apply UPDATE_PRODUCT, then mark work products for
//   rebuild only if the comparison result added personas, features, or user
//   stories (~lines 2502-2515).
// - commitRefinementOnly mirrors the Refinement Only dialog's confirm handler
//   (~lines 2694-2732). NOTE: this differs materially from commitMinorEvolution
//   in the modal today — see the delta callout below.
//
// Unlike the modal (which wraps these calls in try/catch and only logs to
// console, swallowing failures), these staged-mode functions let errors
// propagate so callers can surface/roll back on failure.

import { addUserAnswersApi, applySpecificationChanges, markWorkProductsNeedingRebuild, updateProjectApi, updateUserAnswer } from "@/api/api";
import { NFR_FIELDS } from "../newProduct/state";

import type { RefreshAnswerIds, RefreshCoreValues } from "./types";

/**
 * Write only the core fields that changed between `original` and `edited`:
 * the project row (name/description) via updateProjectApi, and each changed
 * Q-backed field (type/users/problem/solution/team and all eight NFR fields)
 * via updateUserAnswer.
 *
 * Legacy fields whose answerId is null are skipped even if changed (mirrors
 * the modal's `if (answerIds.x)` guards at UpdateProductModal.tsx:929-939).
 * NFR fields instead CREATE their answer row by stable question variable when
 * edited to a non-blank value on a product that predates KAN-1729. Numeric NFR
 * question IDs are environment-specific and must never be hard-coded here.
 */
export async function writeCoreUpdates(args: {
    token: string;
    projectId: number;
    answerIds: RefreshAnswerIds;
    original: RefreshCoreValues;
    edited: RefreshCoreValues;
}): Promise<void> {
    const { token, projectId, answerIds, original, edited } = args;

    if (edited.name !== original.name || edited.description !== original.description) {
        await updateProjectApi(
            {
                project_id: projectId,
                project_name: edited.name,
                project_description: edited.description
            },
            token
        );
    }

    const fieldWrites: Array<{ answerId: number | null; changed: boolean; value: string }> = [
        { answerId: answerIds.type, changed: edited.type !== original.type, value: edited.type },
        { answerId: answerIds.users, changed: edited.users !== original.users, value: edited.users },
        { answerId: answerIds.problem, changed: edited.problem !== original.problem, value: edited.problem },
        { answerId: answerIds.solution, changed: edited.solution !== original.solution, value: edited.solution },
        { answerId: answerIds.team, changed: edited.team !== original.team, value: edited.team }
    ];

    for (const field of fieldWrites) {
        if (!field.changed) continue;
        if (field.answerId) {
            await updateUserAnswer(field.answerId, field.value, token);
        }
    }

    for (const field of NFR_FIELDS) {
        const value = edited.nfr[field.key];
        if (value === original.nfr[field.key]) continue;
        const answerId = answerIds.nfr[field.key];
        if (answerId) {
            await updateUserAnswer(answerId, value, token);
        } else if (value.trim() !== "") {
            await addUserAnswersApi({ projectid: projectId, question_variable: field.variable, sectionid: 1, answer_text: value }, token);
        }
    }
}

function hasAddedContent(comparisonResult: any): boolean {
    const hasAddedPersonas = comparisonResult?.merged_personas?.added_personas?.length > 0;
    const hasAddedFeatures = comparisonResult?.merged_features?.features?.filter((f: any) => f.source === "ADDED")?.length > 0;
    const hasAddedUserStories = comparisonResult?.merged_features?.features?.some((f: any) => f.user_stories?.some((s: any) => s.source === "ADDED"));
    return Boolean(hasAddedPersonas || hasAddedFeatures || hasAddedUserStories);
}

interface CommitArgs {
    token: string;
    projectId: number;
    userId: number;
    answerIds: RefreshAnswerIds;
    original: RefreshCoreValues;
    edited: RefreshCoreValues;
    comparisonResult: unknown;
    originalAnswers: unknown[];
}

/**
 * Commit a MINOR_EVOLUTION verdict.
 *
 * ORDER (staged-write invariant): writeCoreUpdates -> applySpecificationChanges
 * (action UPDATE_PRODUCT) -> markWorkProductsNeedingRebuild, the last step only
 * if the comparison result added personas, features, or user stories (mirrors
 * UpdateProductModal.tsx:2502-2515's hasAddedPersonas/Features/UserStories
 * checks exactly).
 */
export async function commitMinorEvolution(args: CommitArgs): Promise<void> {
    const { token, projectId, userId, answerIds, original, edited, comparisonResult, originalAnswers } = args;

    await writeCoreUpdates({ token, projectId, answerIds, original, edited });

    await applySpecificationChanges(
        {
            projectid: projectId,
            userid: userId,
            action: "UPDATE_PRODUCT",
            comparison_result: comparisonResult,
            original_answers: originalAnswers
        },
        token
    );

    if (hasAddedContent(comparisonResult)) {
        await markWorkProductsNeedingRebuild([projectId], null, null, null, true, token);
    }
}

/**
 * Commit a REFINEMENT_ONLY verdict.
 *
 * Mirrors the modal's Refinement Only dialog confirm handler
 * (UpdateProductModal.tsx:2698-2729), which materially differs from the
 * Minor Evolution handler:
 * - applySpecificationChanges (action UPDATE_PRODUCT) is called ONLY when
 *   the comparison result added personas (`hasAddedPersonas`, line 2704) —
 *   NOT gated on added features/user stories like commitMinorEvolution.
 * - markWorkProductsNeedingRebuild is never called in this path.
 *
 * writeCoreUpdates still runs first (staged-write invariant): the modal
 * itself performs the immediate-write block unconditionally before the scan
 * even starts, so in the staged flow that write is deferred to this commit
 * step, ahead of any apply call.
 */
export async function commitRefinementOnly(args: CommitArgs): Promise<void> {
    const { token, projectId, userId, answerIds, original, edited, comparisonResult, originalAnswers } = args;

    await writeCoreUpdates({ token, projectId, answerIds, original, edited });

    const hasAddedPersonas = (comparisonResult as any)?.merged_personas?.added_personas?.length > 0;

    if (hasAddedPersonas) {
        await applySpecificationChanges(
            {
                projectid: projectId,
                userid: userId,
                action: "UPDATE_PRODUCT",
                comparison_result: comparisonResult,
                original_answers: originalAnswers
            },
            token
        );
    }
}
